import { CoursePackProto_CoursePackSlug_Value } from "@noom/noom-contracts/noom_contracts/backend/program/model/course_pack";
import type {
  B2BEnrollmentEnumsProto_DisqualificationReason,
  B2BEnrollmentEnumsProto_Program,
} from "@noom/noom-contracts/noom_contracts/events/web/b2b/shared";

export const ASSET_URL = `${window.ENV.CDN_URL}/assets`;

export enum ErrorState {
  DEFAULT = "Generic error",
  ACCOUNT_EXISTS = "Account exists",
  SSO_ACCOUNT_EXISTS = "SSO_ACCOUNT_EXISTS",
  BATCH_FULL = "Batch full",
  EXISTING_VIRGIN_PULSE_USER = "Already linked to Virgin Pulse",
  CODE_CLAIMED = "UPID claimed",
  ELIGIBILITY_CHECK_EXISTING_ACCOUNT = "Account with eligibility information already exists",
  FAILED_ELIGIBILITY_CHECK = "FAILED_ELIGIBILITY_CHECK",
  SSO_INELIGIBLE = "SSO_INELIGIBLE",
  SUPPORT_REQUEST_CREATED = "SUPPORT_REQUEST_CREATED",
  PENDING_DELETION_REQUEST = "PENDING_DELETION_REQUEST",
  BEFORE_GOLIVE_DATE = "Partner is not live yet",
  PAST_CONTRACT_END_DATE = "PAST_CONTRACT_END_DATE",
  PRIOR_TO_EFFECTIVE_DATE = "PRIOR_TO_EFFECTIVE_DATE",
  ELIGIBILITY_CHECK_API_ERROR = "ELIGIBILITY_CHECK_API_ERROR",
  ELIGIBILITY_CHECK_MULTIPLE_MATCHES = "ELIGIBILITY_CHECK_MULTIPLE_MATCHES",
  MISSING_AUTOLOGIN = "MISSING_AUTOLOGIN",
}

export enum ErrorCode {
  MED_ENROLLMENT_FAILED = "MED_ENROLLMENT_FAILED",
}

export type ProgramOffering = Exclude<
  B2BEnrollmentEnumsProto_Program,
  "PROGRAM_UNSPECIFIED" | "UNRECOGNIZED"
>;

export type CoursePackSlug = CoursePackProto_CoursePackSlug_Value;

export type DisqualificationReason = Exclude<
  B2BEnrollmentEnumsProto_DisqualificationReason,
  "UNRECOGNIZED" | "DISQUALIFICATION_REASON_UNKNOWN"
>;

export enum ScreenedOutReason {
  BMI_TOO_LOW = "bmi_too_low",
  DPP_DIABETES_TYPE = "dpp_diabetes_type",
  DPP_DM_INELIGIBLE = "dpp_dm_ineligible",
  DPP_RISK_SCORE_TOO_LOW = "dpp_risk_score_too_low",
  EATING_DISORDER = "eating_disorder",
  IDEAL_WEIGHT_BMI_TOO_LOW = "ideal_weight_bmi_too_low",
  NO_PRIMARY_CARE_PROVIDER = "no_primary_care_provider",
  PREGNANT = "pregnant",
}

/**
 * List of states with consumer health laws.
 */
export const CONSUMER_HEALTH_LAW_STATES = ["CT", "NV", "WA"];

export const DOB_FORMAT = "MM/dd/yyyy";

/**
 * Allowlist of questions where the answers do not contain PII. This is used
 * so that we can log user answers to these questions.
 */
export const NON_PII_QUESTIONS = [
  "healthGoal",
  "programOverview",
  "programSelection",
  "scaleOptIn",
  "scaleEarningOptIn",
  "noomMedOverview",
  "noomMedMoreOptions",
  "healthConditions",
  "medications",
  "glp1Medication",
  "hadA1cTested",
  "a1c",
  "mskPain",
  "mskPainLevel",
  "phq1",
  "phq2",
  "phq3",
  "phq4",
  "enrolledInMedicare",
  "medicareConsent",
  "confirmChangeProgramDpp",
  "confirmChangeProgramMed",
  "nutritionBasics",
  "culturalImpacts",
  "eatingMovingAffordably",
  "diabetesDiagnosis",
  "diabetesType",
  "primaryCareProvider",
  "idealWeight",
  "connectHealthRecordsPersonalization",
];

export const NOOM_SUPPORTED_LANGUAGES = [
  "en",
  "en-AU",
  "en-CA",
  "en-GB",
  "en-US",
  "es",
  "de",
  "ko",
];

export const NOOM_SUPPORTED_ENGLISH_LOCALES = NOOM_SUPPORTED_LANGUAGES.filter(
  (language) => language.slice(0, 2) === "en",
);

/**
 * List of states with opt out laws.
 */
export const OPT_OUT_STATES = [
  "CA",
  "CO",
  "CT",
  "DE",
  "IA",
  "MD",
  "MT",
  "MN",
  "NE",
  "NH",
  "NJ",
  "NV",
  "OR",
  "TN",
  "TX",
  "UT",
  "VA",
  "WA",
];
export const OPT_OUT_MHMDA_STATES = ["CT", "NV", "WA"];

export * from "./Curriculum";
export * from "./enrollment";
export * from "./success";
export * from "./survey-answers";
export * from "./retriage";
